import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

interface PersonalizationSettings {
  preferredCurrency: string;
  timeZone: string;
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  privacy: {
    profileVisibility: 'public' | 'private' | 'contacts';
    showOnlineStatus: boolean;
  };
  preferences: {
    compactView: boolean;
  };
}

interface UserBehavior {
  pageViews: Record<string, number>;
  clickPatterns: Record<string, number>;
  searchHistory: string[];
  timeSpent: Record<string, number>;
  engagementScore: number;
  deviceType: 'mobile' | 'desktop' | 'tablet';
}

interface JobRecommendation {
  id: string;
  title: string;
  company: string;
  match: number;
  reason: string;
}

interface Recommendations {
  jobs: JobRecommendation[];
  skills: string[];
}

interface EngagementInsights {
  score: number;
  suggestedActions: string[];
}

interface PersonalizationContextType {
  settings: PersonalizationSettings;
  updateSettings: (updates: Partial<PersonalizationSettings>) => void;
  behavior: UserBehavior;
  recommendations: Recommendations;
  isPersonalizationReady: boolean;
  trackEvent: (event: string, data?: any) => void;
  updateBehavior: (type: string, data: any) => void;
  getEngagementInsights: () => EngagementInsights;
}

const defaultSettings: PersonalizationSettings = {
  preferredCurrency: 'PKR',
  timeZone: 'Asia/Karachi',
  notifications: {
    email: true,
    sms: false,
    push: true,
  },
  privacy: {
    profileVisibility: 'public',
    showOnlineStatus: true,
  },
  preferences: {
    compactView: false,
  },
};

const defaultBehavior: UserBehavior = {
  pageViews: {},
  clickPatterns: {},
  searchHistory: [],
  timeSpent: {},
  engagementScore: 50,
  deviceType: 'desktop',
};

const defaultRecommendations: Recommendations = {
  jobs: [],
  skills: [],
};

const PersonalizationContext = createContext<PersonalizationContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
  behavior: defaultBehavior,
  recommendations: defaultRecommendations,
  isPersonalizationReady: false,
  trackEvent: () => {},
  updateBehavior: () => {},
  getEngagementInsights: () => ({ score: 50, suggestedActions: [] })
});

export const PersonalizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useUser();
  const [settings, setSettings] = useState<PersonalizationSettings>(defaultSettings);
  const [behavior, setBehavior] = useState<UserBehavior>(defaultBehavior);
  const [recommendations] = useState<Recommendations>(defaultRecommendations);
  const [isPersonalizationReady, setIsPersonalizationReady] = useState(false);

  // Load preferences from database when user authenticates
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setIsPersonalizationReady(true);
      return;
    }

    const loadPreferences = async () => {
      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.warn('Failed to load preferences:', error);
          setIsPersonalizationReady(true);
          return;
        }

        if (data) {
          setSettings({
            preferredCurrency: data.preferred_currency || 'PKR',
            timeZone: data.time_zone || 'Asia/Karachi',
            notifications: {
              email: data.notifications_email ?? true,
              sms: data.notifications_sms ?? false,
              push: data.notifications_push ?? true,
            },
            privacy: {
              profileVisibility: (data.profile_visibility as 'public' | 'private' | 'contacts') || 'public',
              showOnlineStatus: data.show_online_status ?? true,
            },
            preferences: {
              compactView: data.compact_view ?? false,
            },
          });

          // Load behavior data
          const behaviorData = (data.behavior_data as Record<string, any>) || {};
          setBehavior({
            pageViews: behaviorData.pageViews || {},
            clickPatterns: behaviorData.clickPatterns || {},
            searchHistory: behaviorData.searchHistory || [],
            timeSpent: behaviorData.timeSpent || {},
            engagementScore: behaviorData.engagementScore || 50,
            deviceType: behaviorData.deviceType || 'desktop',
          });
        }
      } catch (error) {
        console.warn('Failed to load personalization data:', error);
      } finally {
        setIsPersonalizationReady(true);
      }
    };

    loadPreferences();
  }, [isAuthenticated, user?.id]);

  const updateSettings = useCallback(async (updates: Partial<PersonalizationSettings>) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);

    if (!isAuthenticated || !user?.id) return;

    try {
      // Map to database columns
      const dbUpdates: Record<string, any> = {};
      
      if (updates.preferredCurrency) dbUpdates.preferred_currency = updates.preferredCurrency;
      if (updates.timeZone) dbUpdates.time_zone = updates.timeZone;
      if (updates.notifications) {
        dbUpdates.notifications_email = updates.notifications.email;
        dbUpdates.notifications_sms = updates.notifications.sms;
        dbUpdates.notifications_push = updates.notifications.push;
      }
      if (updates.privacy) {
        dbUpdates.profile_visibility = updates.privacy.profileVisibility;
        dbUpdates.show_online_status = updates.privacy.showOnlineStatus;
      }
      if (updates.preferences) {
        dbUpdates.compact_view = updates.preferences.compactView;
      }

      await supabase
        .from('user_preferences')
        .update(dbUpdates as never)
        .eq('user_id', user.id);
    } catch (error) {
      console.warn('Failed to save personalization settings:', error);
    }
  }, [settings, isAuthenticated, user?.id]);

  const trackEvent = useCallback(async (event: string, data?: any) => {
    const newBehavior = {
      ...behavior,
      clickPatterns: {
        ...behavior.clickPatterns,
        [event]: (behavior.clickPatterns[event] || 0) + 1
      }
    };
    setBehavior(newBehavior);

    if (!isAuthenticated || !user?.id) return;

    try {
      await supabase
        .from('user_preferences')
        .update({
          behavior_data: {
            pageViews: newBehavior.pageViews,
            clickPatterns: newBehavior.clickPatterns,
            searchHistory: newBehavior.searchHistory,
            timeSpent: newBehavior.timeSpent,
            engagementScore: newBehavior.engagementScore,
            deviceType: newBehavior.deviceType,
          }
        })
        .eq('user_id', user.id);
    } catch (error) {
      console.warn('Failed to save behavior data:', error);
    }
  }, [behavior, isAuthenticated, user?.id]);

  const updateBehavior = useCallback((type: string, data: any) => {
    setBehavior(prev => {
      const updated = { ...prev };
      
      if (type === 'pageView') {
        updated.pageViews = {
          ...prev.pageViews,
          [data.page]: (prev.pageViews[data.page] || 0) + 1
        };
      }
      
      if (type === 'timeSpent') {
        updated.timeSpent = {
          ...prev.timeSpent,
          [data.page]: data.duration
        };
      }
      
      return updated;
    });
  }, []);

  const getEngagementInsights = useCallback((): EngagementInsights => {
    return {
      score: behavior.engagementScore,
      suggestedActions: [
        'Complete your profile to get better job matches',
        'Upload a professional photo',
        'Add more skills to your profile'
      ]
    };
  }, [behavior.engagementScore]);

  return (
    <PersonalizationContext.Provider value={{
      settings,
      updateSettings,
      behavior,
      recommendations,
      isPersonalizationReady,
      trackEvent,
      updateBehavior,
      getEngagementInsights
    }}>
      {children}
    </PersonalizationContext.Provider>
  );
};

export const usePersonalization = () => {
  const context = useContext(PersonalizationContext);
  return context;
};
